import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Menu, Play, X } from "lucide-react";

const MENU_LINKS = [
  { id: "home", label: "Home" },
  { id: "tools", label: "Our Tools" },
  { id: "about", label: "About Proxima" },
  { id: "contact", label: "Contact" },
];
import Seo from "@/components/Seo";
import { FadeIn, MaskText, SectionTag } from "@/components/Reveal";
import Footer from "@/components/Footer";

const PILLARS = [
  {
    num: "01",
    title: "Born From A Real Problem",
    body: "PPS exists because the work demanded it. It started as a problem on a real jobsite — not an idea in a boardroom.",
  },
  {
    num: "02",
    title: "The First Flagship",
    body: "PrePipe Station leads the Proxima Centauri lineup. More tools are already on the drawing board.",
  },
  {
    num: "03",
    title: "A New American Tool Company",
    body: "Built on real construction jobsite experience — and built to stand shoulder to shoulder with the trades.",
  },
];

const SPEC_SLOTS = ["01", "02", "03", "04"];

const PpsPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
  <div data-testid="pps-page" className="relative min-h-screen overflow-hidden bg-coal">
    <Seo
      title="PrePipe Station (PPS) — Proxima Centauri Tools"
      siteName="Proxima Centauri Tools"
      description="PrePipe Station (PPS) is the first flagship tool from Proxima Centauri Tools, born from a real problem found on a construction jobsite. Full reveal coming soon."
      image="/logo.png"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "Product",
        name: "PrePipe Station (PPS)",
        brand: { "@type": "Brand", name: "Proxima Centauri Tools" },
      }}
    />

    <div className="absolute inset-0" aria-hidden="true">
      <div className="absolute inset-0 bg-[#0b0b0e]" />
      <div className="brushed-metal absolute inset-0 opacity-70" />
      <div className="absolute inset-0 bg-[radial-gradient(55%_40%_at_50%_0%,rgba(216,222,232,0.12),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(45%_35%_at_50%_20%,rgba(230,0,0,0.14),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(125%_90%_at_50%_30%,transparent_55%,rgba(5,5,5,0.55)_100%)]" />
    </div>

    <header className="relative z-20 mx-auto flex max-w-[1600px] items-center justify-between px-6 py-6 md:px-12">
      <Link to="/" data-testid="pps-logo-link" className="group flex items-center gap-3" aria-label="Proxima Centauri Tools — home">
        <img
          src="/logo.png"
          alt="Proxima Centauri Tools logo"
          className="h-16 w-16 object-cover transition-[filter] duration-500 group-hover:drop-shadow-[0_0_14px_rgba(230,0,0,0.7)] md:h-20 md:w-20"
        />
        <span className="hidden flex-col leading-none sm:flex">
          <span className="font-display text-2xl font-semibold uppercase tracking-wider text-white">Proxima Centauri</span>
          <span className="font-display text-base uppercase tracking-[0.5em] text-ember">Tools</span>
        </span>
      </Link>
      <Link
        to="/"
        data-testid="pps-back-link"
        className="group hidden items-center gap-2 font-display text-lg uppercase tracking-[0.18em] text-ash transition-colors duration-300 hover:text-white md:flex"
      >
        <ArrowLeft size={18} className="text-ember transition-transform duration-300 group-hover:-translate-x-1" />
        Back To Site
      </Link>
      <button
        data-testid="pps-mobile-menu-open"
        onClick={() => setMenuOpen(true)}
        aria-label="Open menu"
        className="flex h-11 w-11 items-center justify-center border border-white/10 bg-coal/40 backdrop-blur-md md:hidden"
      >
        <Menu size={22} className="text-white" />
      </button>
    </header>

    <AnimatePresence>
      {menuOpen && (
        <motion.div
          data-testid="pps-mobile-menu"
          className="fixed inset-0 z-[100] flex flex-col bg-coal/95 backdrop-blur-2xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-between px-6 py-6">
            <img src="/logo.png" alt="Proxima Centauri Tools logo" className="h-16 w-16 object-cover" />
            <button
              data-testid="pps-mobile-menu-close"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="text-white"
            >
              <X size={28} />
            </button>
          </div>
          <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
            {MENU_LINKS.map((link, i) => (
              <motion.div
                key={link.id}
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
              >
                <Link
                  data-testid={`pps-mobile-nav-${link.id}`}
                  to={`/#${link.id}`}
                  onClick={() => setMenuOpen(false)}
                  className="block text-left font-display text-6xl font-semibold uppercase leading-[1.05] text-white/90"
                >
                  <span className="mr-3 text-xl text-ember">0{i + 1}</span>
                  {link.label}
                </Link>
              </motion.div>
            ))}
          </nav>
          <p className="px-8 pb-10 font-body text-xs uppercase tracking-[0.3em] text-ash">
            Tools Built From Real Jobsite Problems
          </p>
        </motion.div>
      )}
    </AnimatePresence>

    <main className="relative z-10 mx-auto max-w-[1600px] px-6 pb-28 md:px-12">
      <div className="pt-14 md:pt-20">
        <SectionTag index="PPS-01" label="Flagship — In Development" />
        <h1 data-testid="pps-heading" className="mt-8 font-display font-semibold uppercase leading-[0.9] tracking-tight">
          <MaskText
            lines={[
              <span key="1" className="text-[clamp(3.6rem,10vw,10rem)] text-white">PrePipe</span>,
              <span key="2" className="text-[clamp(3.6rem,10vw,10rem)] metal-sheen">Station</span>,
            ]}
          />
        </h1>
        <FadeIn delay={0.3}>
          <p className="mt-4 font-display text-2xl uppercase tracking-[0.5em] text-ember red-glow-text md:text-3xl">PPS</p>
          <p className="mt-8 max-w-2xl font-body text-base font-light leading-relaxed text-ash md:text-lg">
            Our first flagship tool, born from a real problem found on the jobsite. The full
            reveal is coming — this page will carry the official film, photography and
            specification sheet the moment it drops.
          </p>
        </FadeIn>
      </div>

      <FadeIn className="mt-20" delay={0.1}>
        <div
          data-testid="pps-media-placeholder"
          className="relative overflow-hidden border border-white/10"
        >
          <span className="absolute left-0 top-0 z-10 h-8 w-8 border-l-2 border-t-2 border-ember" aria-hidden="true" />
          <span className="absolute bottom-0 right-0 z-10 h-8 w-8 border-b-2 border-r-2 border-ember" aria-hidden="true" />
          <img
            data-testid="pps-page-image"
            src="/pps-station.jpg"
            alt="PrePipe Station (PPS) on a jobsite workbench surrounded by copper fittings and drills"
            className="block h-auto w-full"
          />
        </div>
        <p className="mt-5 flex items-center gap-3 font-body text-[11px] uppercase tracking-[0.35em] text-ash">
          <Play size={14} className="text-ember/70" />
          Product film and more photos — at reveal
        </p>
      </FadeIn>

      <div className="mt-24 grid grid-cols-1 gap-px bg-white/10 md:grid-cols-3">
        {PILLARS.map((pillar, i) => (
          <FadeIn key={pillar.num} delay={i * 0.12} className="bg-coal">
            <article
              data-testid={`pps-pillar-${pillar.num}`}
              className="group flex h-full flex-col p-10 transition-[background-color] duration-500 hover:bg-carbon"
            >
              <span className="font-display text-6xl font-semibold leading-none text-stroke-silver transition-colors duration-500 group-hover:text-ember group-hover:[-webkit-text-stroke:0px]">
                {pillar.num}
              </span>
              <h2 className="mt-8 font-display text-3xl font-semibold uppercase leading-tight text-white">{pillar.title}</h2>
              <p className="mt-4 font-body text-sm font-light leading-relaxed text-ash">{pillar.body}</p>
            </article>
          </FadeIn>
        ))}
      </div>

      <FadeIn className="mt-24" delay={0.1}>
        <div data-testid="pps-spec-panel" className="border border-white/10 bg-carbon/60 p-8 md:p-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-display text-4xl font-semibold uppercase leading-none text-white md:text-5xl">
              Full Spec Sheet
            </h2>
            <p className="font-body text-[11px] uppercase tracking-[0.35em] text-ash">
              Published at the official reveal — no guesses, no hype
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {SPEC_SLOTS.map((slot) => (
              <div key={slot} data-testid={`pps-spec-slot-${slot}`} className="flex items-center justify-between bg-coal px-6 py-6">
                <span className="font-body text-xs uppercase tracking-[0.3em] text-ash">Spec {slot}</span>
                <span className="font-display text-2xl text-ember/70">—</span>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>

      <FadeIn className="mt-24" delay={0.1}>
        <div className="flex flex-col items-start justify-between gap-10 border border-ember/30 bg-gradient-to-r from-ember/10 via-transparent to-transparent p-10 md:flex-row md:items-center md:p-14">
          <div>
            <h2 className="font-display text-5xl font-semibold uppercase leading-[0.95] text-white md:text-6xl">
              Be First <span className="text-ember red-glow-text">To Know.</span>
            </h2>
            <p className="mt-5 max-w-md font-body text-base font-light leading-relaxed text-ash">
              Full reveal coming soon. Send a message and we'll keep you close to the build.
            </p>
          </div>
          <Link
            to="/#contact"
            data-testid="pps-notify-cta"
            className="group relative inline-flex overflow-hidden bg-ember px-9 py-4 font-display text-xl font-semibold uppercase tracking-[0.15em] text-white transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_44px_-6px_rgba(230,0,0,0.65)]"
          >
            <span className="relative z-10 flex items-center gap-2">
              Get Notified <ArrowUpRight size={20} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
            <span className="absolute inset-0 -translate-x-full bg-white/20 transition-transform duration-500 group-hover:translate-x-0" />
          </Link>
        </div>
      </FadeIn>
    </main>

    <Footer />
  </div>
  );
};

export default PpsPage;
